import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AUTH_ROUTES } from '../../constants/authRoutes';
import appConstants from '../../constants/appConstants';
import { buildAuthPath, getAuthContextFromSearch } from '../../utils/authUtils';
import AuthSplitLayout from '../../views/Users/presentational/AuthSplitLayout';
import { EyeIcon, EyeOffIcon, MailIcon, PasswordRuleIcon, SuccessIcon } from './authIcons';
import './auth.scss';

export function AuthPage({ children }) {
    return <AuthSplitLayout>{children}</AuthSplitLayout>;
}

export function AuthHead({ title, subtitle }) {
    return (
        <div className="auth-head">
            <h1>{title}</h1>
            {subtitle ? <p>{subtitle}</p> : null}
        </div>
    );
}

export function AuthForm({ onSubmit, children }) {
    return (
        <form className="auth-form" onSubmit={onSubmit}>
            {children}
        </form>
    );
}

export function AuthFieldHint({ error }) {
    if (!error) {
        return <div className="auth-field-hint" aria-live="polite" />;
    }
    return (
        <div className="auth-field-hint error" role="alert">
            <span>{error}</span>
        </div>
    );
}

export function AuthTextField({
    id,
    label,
    value,
    onChange,
    onBlur,
    placeholder,
    error,
    autoComplete,
    inputMode,
    type = 'text',
    showHint = true,
}) {
    return (
        <div className="auth-field">
            <label className="auth-field-label" htmlFor={id}>
                {label}
            </label>
            <input
                id={id}
                name={id}
                type={type}
                value={value}
                onChange={(event) => onChange(event.target.value)}
                onBlur={onBlur}
                placeholder={placeholder}
                autoComplete={autoComplete}
                inputMode={inputMode}
                className={`auth-field-input${error ? ' error' : ''}`}
            />
            {showHint ? <AuthFieldHint error={error} /> : null}
        </div>
    );
}

export function AuthPasswordField({
    id,
    label,
    value,
    onChange,
    onBlur,
    placeholder,
    error,
    autoComplete,
    showLabel,
    hideLabel,
    showHint = true,
}) {
    const [visible, setVisible] = useState(false);

    return (
        <div className="auth-field">
            <label className="auth-field-label" htmlFor={id}>
                {label}
            </label>
            <div className={`auth-field-input-row${error ? ' error' : ''}`}>
                <input
                    id={id}
                    name={id}
                    type={visible ? 'text' : 'password'}
                    value={value}
                    onChange={(event) => onChange(event.target.value)}
                    onBlur={onBlur}
                    placeholder={placeholder}
                    autoComplete={autoComplete}
                />
                <button
                    type="button"
                    className="icon-btn-inline"
                    onClick={() => setVisible((current) => !current)}
                    aria-label={visible ? hideLabel : showLabel}
                >
                    {visible ? <EyeIcon /> : <EyeOffIcon />}
                </button>
            </div>
            {showHint ? <AuthFieldHint error={error} /> : null}
        </div>
    );
}

export function AuthPrimaryButton({ children, disabled, type = 'submit', onClick }) {
    return (
        <button
            type={type}
            disabled={disabled}
            onClick={onClick}
            className="auth-btn auth-btn-primary"
        >
            {children}
        </button>
    );
}

export function AuthSecondaryButton({ children, disabled, type = 'button', onClick }) {
    return (
        <button
            type={type}
            disabled={disabled}
            onClick={onClick}
            className="auth-btn auth-btn-secondary"
        >
            {children}
        </button>
    );
}

export function AuthBackToSignIn() {
    const location = useLocation();
    const authContext = getAuthContextFromSearch(location.search);

    return (
        <Link className="auth-btn auth-btn-secondary" to={buildAuthPath(AUTH_ROUTES.LOGIN, authContext)}>
            {appConstants.auth.common.backToSignIn}
        </Link>
    );
}

export function AuthRequirementList({ items }) {
    return (
        <ul className="auth-req-list">
            {items.map((item) => (
                <li key={item.label} className={`auth-req${item.ok ? ' met' : ''}`}>
                    <PasswordRuleIcon ok={item.ok} />
                    {item.label}
                </li>
            ))}
        </ul>
    );
}

export function buildEmailHighlightSubtitle(template, email) {
    const [before, after] = template.split('{email}');
    return (
        <>
            {before}
            <strong>{email}</strong>
            {after || ''}
        </>
    );
}

export function AuthStatusBlock({ variant = 'mail', title, subtitle, expiry }) {
    return (
        <div className="auth-status-block">
            {variant === 'mail' ? (
                <div className="auth-info-glyph">
                    <div className="auth-mail-icon-wrap">
                        <MailIcon />
                    </div>
                </div>
            ) : (
                <div className="auth-success-glyph">
                    <div className="auth-success-icon-wrap">
                        <SuccessIcon />
                    </div>
                </div>
            )}
            <h2>{title}</h2>
            <p>{subtitle}</p>
            {expiry ? <p className="auth-status-expiry">{expiry}</p> : null}
        </div>
    );
}

export function AuthActionsStack({ children }) {
    return <div className="auth-actions-stack">{children}</div>;
}

export function AuthMutedFoot({ prompt, linkLabel, onLinkClick, linkTo }) {
    const link = linkTo ? (
        <Link className="auth-link" to={linkTo}>
            {linkLabel}
        </Link>
    ) : (
        <button type="button" className="auth-link" onClick={onLinkClick}>
            {linkLabel}
        </button>
    );

    return (
        <p className="auth-muted-foot">
            {prompt} {link}
        </p>
    );
}

export function AuthAlertMessage({ message }) {
    return (
        <p className="auth-muted-foot auth-alert" role="alert">
            {message}
        </p>
    );
}